import useFindOrder from "@repo/shopify/hooks/useFindOrder";
import useOrderInfo from "@repo/shopify/hooks/useOrderInfo";
import useOrderPayMethod from "@repo/shopify/hooks/useOrderPayMethod";
import useTracking from "@repo/shopify/hooks/useTracking";
import { formatOrderAddress } from "@repo/shopify/utils/address";
import { extractId } from "@repo/shopify/utils/common";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { Trans } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import Bracket from "@atoms/Bracket";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import Address from "@molecules/Address";
import OrderLineItem from "@molecules/OrderLineItem";

import OrderPaymentInfo from "@organisms/OrderPaymentInfo";

import { URLS } from "@const/urls";

import useCartQuery from "@hooks/useCartQuery";
import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

import { formatAutoDiscountTitle } from "@utils/discount";

export default function OrderCompleted() {
  const navigate = useNavigate();
  const { orderId } = useParams();

  const { lang } = useLanguage();

  const { gtmPurchase } = useTracking();

  const { showFooter } = useLayoutStore();
  const { token, customer } = useCustomerStore();

  const { createCartInput } = useCartQuery();

  const { order, loading } = useFindOrder({ orderId, accessToken: token.accessToken, fetchFirst: 1 });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (loading || !orderId) return;

    if (!order) {
      navigate(URLS.NOT_FOUND, { replace: true });
      return;
    }

    gtmPurchase(order, extractId(customer.id), customer.email ?? undefined);
    showFooter(true);

    void createCartInput({
      variables: { input: { buyerIdentity: { customerAccessToken: token.accessToken }, lines: [] } },
    });
  }, [orderId, order, loading, token]);

  // 결제 수단 정보
  const paymentInfo = useOrderPayMethod({ lang, order });

  const {
    shippingAmount,
    taxAmount,
    dutyAmount,
    totalAmount,
    subTotalAmount,
    codeDiscountAmount,
    autoDiscountAmount,
    codeDiscountTitle,
    autoDiscountTitle,
    shippingTitle,
  } = useOrderInfo(order);

  // 배송지 정보
  const shippingAddress = formatOrderAddress({ address: order?.shippingAddress, lang });
  const billingAddress = formatOrderAddress({ address: order?.billingAddress, lang });

  const estimatedDeliveryRaw = order?.customAttributes.find((a) => a.key === "estimatedDelivery")?.value;
  const estimatedDeliveryDate = estimatedDeliveryRaw ? dayjs(estimatedDeliveryRaw).format("MMM DD, YYYY") : null;

  return (
    <>
      {!order ? (
        <Loading type="page" />
      ) : (
        <div className="pc:col-span-12 col-span-8 flex flex-col gap-2.5">
          <Bracket>{order.name}</Bracket>
          <section className="flex flex-col gap-5">
            <h2 className="text-title grow">Order Completed ({order.name || ""})</h2>
            <section className="tnt-gap-x flex flex-col gap-y-5">
              <section className="pc:col-span-12 col-span-8 flex flex-col gap-y-5">
                <p>
                  <Trans i18nKey="order_completed.description" />
                </p>
                <Link as="square" href={`${URLS.COLLECTIONS_DETAIL}/new`}>
                  Continue Shopping
                </Link>
              </section>
              <section className="pc:col-span-12 pc:grid-cols-12 tnt-gap-x col-span-8 grid grid-cols-6 gap-y-10">
                <section className="pc:col-span-4 col-span-8">
                  <div className="pc:grid-cols-4 grid grid-cols-6 gap-x-2 gap-y-5">
                    <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                      <span className="uppercase">Shipping Method</span>
                      <span className="font-normal">{shippingTitle}</span>
                    </div>
                    <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                      <span className="uppercase">Payment Detail</span>
                      <span className="font-normal">{paymentInfo}</span>
                    </div>
                    <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                      <span className="uppercase">Shipping Address</span>
                      <Address className="font-normal" {...shippingAddress} />
                    </div>
                    <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                      <span className="uppercase">Billing Address</span>
                      <Address className="font-normal" {...billingAddress} />
                    </div>
                    {order.fulfillmentStatus !== "FULFILLED" && estimatedDeliveryDate ? (
                      <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                        <span className="uppercase">Estimated Delivery Date</span>
                        <span className="font-normal">{estimatedDeliveryDate}</span>
                      </div>
                    ) : null}
                  </div>
                </section>
                <section className="tnt-gap-x pc:col-start-7 pc:gap-y-5 col-span-8 grid grid-cols-6 gap-y-2">
                  <ul className="pc:gap-y-5 col-span-8 flex flex-col gap-y-2">
                    {order?.lineItems.edges
                      ?.filter(({ node }) => !node.variant?.product.tags.includes("Gift"))
                      .map(({ node }) => {
                        if (!node.variant) return null;
                        return <OrderLineItem key={node.variant.id} node={node} />;
                      })}

                    {order?.lineItems.edges
                      ?.filter(({ node }) => node.variant?.product.tags.includes("Gift"))
                      .map(({ node }) => {
                        if (!node.variant) return null;
                        return <OrderLineItem key={node.variant.id} node={node} />;
                      })}
                  </ul>
                  <div className="pc:col-span-2 pc:col-start-7 col-span-3 col-start-4 flex flex-col gap-y-1">
                    <OrderPaymentInfo
                      autoDiscountAmount={autoDiscountAmount}
                      autoDiscountTitle={formatAutoDiscountTitle(autoDiscountTitle)}
                      codeDiscountAmount={codeDiscountAmount}
                      codeDiscountTitle={codeDiscountTitle}
                      dutyAmount={dutyAmount}
                      originalTotalAmount={subTotalAmount}
                      shippingFee={shippingAmount}
                      taxAmount={taxAmount}
                      totalAmount={totalAmount}
                    />
                  </div>
                </section>
              </section>
            </section>
          </section>
        </div>
      )}
    </>
  );
}
